import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, animate } from "framer-motion";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { useContent, useSettings } from "@/lib/api";
import { Countdown } from "@/components/site/Live";
import { Chip, Reveal, SecTitle, StatusPanel, Wrap } from "@/night/ui";

const CLIPS = ["/loading/falcon.mp4", "/loading/city1.mp4", "/loading/city2.mp4", "/loading/city3.mp4", "/loading/city4.mp4"];

function HeroVideo() {
  const [i, setI] = useState(0);
  return (
    <video key={i} src={CLIPS[i]} autoPlay muted playsInline onEnded={() => setI((i + 1) % CLIPS.length)} poster="/site/sora-hero-eagle.jpg"
      className="absolute inset-0 -z-20 h-full w-full object-cover" data-testid="hero-video" />
  );
}

function Hero() {
  const { data: s } = useSettings();
  const line = { hidden: { y: "110%" }, show: (d) => ({ y: 0, transition: { duration: 1, delay: d, ease: [0.2, 0.8, 0.2, 1] } }) };
  return (
    <section className="relative isolate flex min-h-[100svh] items-end overflow-hidden" data-testid="hero">
      <HeroVideo />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#030509] via-[#030509]/50 to-[#030509]/30" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-l from-[#030509]/80 via-transparent to-transparent" />
      <div className="scan -z-10" /><div className="vignette -z-10" />
      <div aria-hidden className="f-lat outline-word pointer-events-none absolute left-0 right-0 top-[16%] -z-10 select-none text-center text-[26vw] leading-none opacity-25" dir="ltr">SORA</div>
      <Wrap className="w-full pb-16 pt-40">
        <div className="grid items-end gap-12 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="flex items-center gap-4">
              <span className="siren w-14" /><span className="kicker">WELCOME TO LOS SANTOS · SORA RP</span>
            </motion.div>
            <h1 className="f-ar mt-6 text-[13vw] leading-[1.05] sm:text-7xl lg:text-[88px]" data-testid="hero-title">
              <span className="block overflow-hidden pb-2"><motion.span className="block" variants={line} initial="hidden" animate="show" custom={0.3}>سورا وطن بلا حدود</motion.span></span>
              <span className="block overflow-hidden pb-2"><motion.span className="glow-text block" variants={line} initial="hidden" animate="show" custom={0.5}>فكن أنت بلا حدود</motion.span></span>
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }} className="mt-6 max-w-xl text-lg leading-9 text-slate-300">
              فوق كل القوانين المعتادة، تحت سماء لا تحدها حدود. هنا يبدأ SORA، وهنا تبدأ قصتك.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }} className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/apply" className="btn-ice !px-8 !py-4 !text-base" data-testid="hero-apply-btn">ابدأ قصتك، قدّم الحين</Link>
              <Link to="/departments" className="btn-ghost !px-7 !py-4" data-testid="hero-departments-btn">استعرض الأقسام <ArrowLeft size={16} /></Link>
            </motion.div>
            <div className="mt-10"><Countdown openAt={s?.open_at} /></div>
          </div>
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.2, duration: 0.8 }} className="lg:justify-self-end"><StatusPanel /></motion.div>
        </div>
        <div className="mt-14 flex items-center justify-center gap-2 text-xs text-slate-500"><ChevronDown size={16} className="animate-bounce" />انزل تحت</div>
      </Wrap>
    </section>
  );
}

function Ticker() {
  const { data: news = [] } = useContent("news");
  const items = [...news.map((n) => n.title), "التقديم على المدينة مفتوح", "رول بلاي واقعي 24/7", "LSPD · EMS · FIRE · COURT · GANG"];
  const row = [...items, ...items];
  return (
    <div className="relative overflow-hidden border-y border-[var(--n-line)] bg-[var(--n-cyan)] py-3 text-[#00131a]" data-testid="ticker">
      <div className="marquee gap-12">
        {[...row, ...row].map((t, i) => <span key={i} className="f-ar flex items-center gap-12 whitespace-nowrap text-xl">{t}<span className="text-sm">✦</span></span>)}
      </div>
    </div>
  );
}

function Counter({ to, suffix = "", prefix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, to, { duration: 1.6, ease: "easeOut", onUpdate: (v) => { if (ref.current) ref.current.textContent = `${prefix}${Math.round(v)}${suffix}`; } });
    return () => c.stop();
  }, [inView, to, prefix, suffix]);
  return <span ref={ref} dir="ltr">{prefix}0{suffix}</span>;
}

function Stats() {
  const S = [[<Counter to={50} prefix="+" />, "وظيفة وسيناريو داخل المدينة", "JOBS"], [<span dir="ltr"><Counter to={24} />/7</span>, "طاقم إداري متواجد", "SUPPORT"], [<Counter to={5} />, "أقسام رسمية للانضمام", "FACTIONS"], [<Counter to={128} />, "خانة لاعب في السيرفر", "SLOTS"]];
  return (
    <Wrap className="grid grid-cols-2 border-x border-b border-[var(--n-line)] lg:grid-cols-4">
      {S.map(([n, l, en], i) => (
        <Reveal key={en} delay={i * 0.08} className="border-[var(--n-line)] p-8 odd:border-l lg:border-l lg:last:border-l-0" >
          <div className="kicker !text-[10px] !text-slate-500">{en}</div>
          <div className="f-lat mt-3 text-6xl leading-none text-white" data-testid="home-stat">{n}</div>
          <div className="mt-3 text-sm text-[var(--n-mute)]">{l}</div>
        </Reveal>
      ))}
    </Wrap>
  );
}

function Departments() {
  const { data = [] } = useContent("departments");
  const [on, setOn] = useState(0);
  return (
    <Wrap className="mt-32">
      <SecTitle n="01" en="FACTIONS" ar="اختر طريقك في المدينة"><Link to="/departments" className="btn-ghost" data-testid="home-all-depts">كل الأقسام <ArrowLeft size={16} /></Link></SecTitle>
      <div className="dept-acc hidden md:flex" onMouseLeave={() => setOn(0)}>
        {data.map((d, i) => (
          <Link key={d.slug} to="/departments" onMouseEnter={() => setOn(i)} className={`border border-[var(--n-line)] ${on === i ? "on" : ""}`} data-testid={`home-dept-${d.slug}`}>
            <img src={d.image} alt={d.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#030509] via-[#030509]/50 to-[#030509]/10" />
            <span className="vtag f-lat text-4xl tracking-[0.25em] text-white/85">{d.tag}</span>
            <div className="info absolute inset-x-0 bottom-0 p-8">
              <div className="flex items-center gap-3"><span className="f-mono text-xs text-[var(--n-cyan)]">0{i + 1}</span><Chip>{d.tag}</Chip>{d.soon && <Chip tone="gold">قريباً</Chip>}</div>
              <h3 className="f-ar mt-4 text-4xl">{d.name}</h3>
              <p className="mt-3 line-clamp-2 max-w-lg text-sm leading-7 text-slate-300">{d.description}</p>
            </div>
          </Link>
        ))}
      </div>
      <div className="grid gap-4 md:hidden">
        {data.map((d) => (
          <Link key={d.slug} to="/departments" className="relative h-56 overflow-hidden border border-[var(--n-line)]" data-testid={`home-dept-m-${d.slug}`}>
            <img src={d.image} alt={d.name} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#030509] to-transparent" />
            <div className="absolute bottom-5 right-5"><Chip>{d.tag}</Chip><h3 className="f-ar mt-2 text-3xl">{d.name}</h3></div>
          </Link>
        ))}
      </div>
    </Wrap>
  );
}

function Showcase() {
  const { data: cars = [] } = useContent("cars");
  const c = cars.find((x) => x.id === "bugatti") || cars[0];
  if (!c) return null;
  return (
    <section className="relative mt-32 overflow-hidden border-y border-[var(--n-line)]">
      <img src={c.image} alt={c.name} className="absolute inset-0 -z-10 h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-l from-[#030509] via-[#030509]/80 to-transparent" />
      <Wrap className="grid gap-10 py-24 lg:grid-cols-2">
        <Reveal>
          <span className="kicker">02 · SHOWROOM</span>
          <div className="f-lat mt-4 text-7xl leading-none text-white sm:text-8xl" dir="ltr" style={{ textAlign: "right" }}>{c.brand}</div>
          <h2 className="f-ar mt-2 text-4xl text-[var(--n-cyan)]">{c.name}</h2>
          <p className="mt-6 max-w-md leading-8 text-slate-300">{c.description}</p>
          <div className="mt-8 flex gap-10">
            <div><div className="text-xs text-slate-500">أقصى سرعة</div><div className="f-lat mt-1 text-4xl">{c.topSpeed}</div></div>
            <div><div className="text-xs text-slate-500">السعر</div><div className="f-lat mt-1 text-4xl text-[var(--n-cyan)]">{c.price}</div></div>
          </div>
          <Link to="/cars" className="btn-ice mt-10" data-testid="home-showroom-btn">ادخل المعرض <ArrowLeft size={16} /></Link>
        </Reveal>
      </Wrap>
    </section>
  );
}

function News() {
  const { data = [] } = useContent("news");
  if (!data.length) return null;
  return (
    <Wrap className="mt-32">
      <SecTitle n="03" en="DISPATCH" ar="آخر الأخبار"><Link to="/news" className="btn-ghost" data-testid="home-all-news">كل الأخبار <ArrowLeft size={16} /></Link></SecTitle>
      <div className="grid gap-px bg-[var(--n-line)] md:grid-cols-2">
        {data.slice(0, 2).map((n, i) => (
          <Reveal key={n.id} delay={i * 0.1} className="bg-[var(--n-void)]">
            <Link to="/news" className="group block p-10 transition-colors hover:bg-[var(--n-ink)]" data-testid={`home-news-${n.id}`}>
              <div className="flex items-center gap-3"><Chip>{n.tag}</Chip><span className="f-mono text-xs text-slate-500">{n.date}</span></div>
              <h3 className="f-ar mt-6 text-3xl transition-colors group-hover:text-[var(--n-cyan)]">{n.title}</h3>
              <p className="mt-4 line-clamp-2 leading-8 text-[var(--n-mute)]">{n.body}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </Wrap>
  );
}

function Cta() {
  return (
    <section className="relative mt-32 overflow-hidden">
      <video src="/loading/city2.mp4" autoPlay muted loop playsInline className="absolute inset-0 -z-10 h-full w-full object-cover opacity-50" />
      <div className="absolute inset-0 -z-10 bg-[#030509]/60" /><div className="vignette -z-10" />
      <Wrap className="py-32 text-center">
        <Reveal>
          <img src="/site/logo.png" alt="" className="mx-auto h-28 w-28 object-contain drop-shadow-[0_0_30px_rgba(0,225,255,0.7)]" />
          <h2 className="f-ar mt-8 text-5xl sm:text-7xl">جاهز تبدأ قصتك؟</h2>
          <p className="mx-auto mt-5 max-w-lg text-lg leading-9 text-slate-300">اقرأ القوانين، اختر قسمك، وخلّ اسمك ينذكر في لوس سانتوس.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/apply" className="btn-ice !px-8 !py-4" data-testid="home-cta-apply">قدّم على المدينة</Link>
            <Link to="/rules" className="btn-ghost !px-8 !py-4" data-testid="home-cta-rules">قوانين المدينة</Link>
          </div>
        </Reveal>
      </Wrap>
    </section>
  );
}

export default function Home() {
  return (<><Hero /><Ticker /><Stats /><Departments /><Showcase /><News /><Cta /></>);
}
